import { Search, SlidersHorizontal, X } from "lucide-react";
import { useId, useMemo } from "react";

import { ToggleChip } from "components/ToggleChip";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";

import {
  activeFilterCount,
  NO_FILTERS,
  type DirectoryFilters,
} from "./directory-filters";
import type { Signal, Track } from "./schemas";
import { sortSignals } from "./signal-utils";

// 44px tall: the chips are the primary control on a phone, and the default
// ToggleChip is sized for a dense desktop form.
const CHIP = "min-h-11 rounded-full px-4 text-[0.9375rem]";
// Chip states on the Dusk tokens. ToggleChip's own states use the lightened
// text blue as a fill, which is too pale for white text on the dark surface.
export const CHIP_ON = "border-lit bg-hb-blue-fill text-white";
export const CHIP_OFF =
  "border-border bg-card text-foreground hover:border-lit hover:text-foreground";
const chipState = (selected: boolean) => (selected ? CHIP_ON : CHIP_OFF);
// In the quick row a chip never wraps or shrinks: the row scrolls instead.
const ROW_CHIP = `${CHIP} shrink-0 whitespace-nowrap`;

// Native selects on purpose: on a phone they open the platform's own picker,
// which handles a list of a hundred company names, or a signal label of a
// whole sentence, better than a popover can. The closed select clips a long
// choice with an ellipsis instead of widening the panel.
const SELECT =
  "h-11 w-full min-w-0 text-ellipsis rounded-md border border-input bg-background px-3 text-base text-foreground sm:text-sm focus:border-lit focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-lit/25";

/**
 * The search field and the filters of the Attendees tab. The three quick chips
 * sit in one row that scrolls sideways on a phone and wraps from `sm` up; the
 * panel behind "Filters" is unchanged.
 */
export function AttendeeFilters({
  filters,
  onChange,
  panelOpen,
  onPanelOpenChange,
  signals,
  tracks,
  companies,
  canMatchMySignals,
  canMatchMyTrack,
}: {
  filters: DirectoryFilters;
  onChange: (next: DirectoryFilters) => void;
  panelOpen: boolean;
  onPanelOpenChange: (open: boolean) => void;
  signals: Signal[];
  tracks: Track[];
  companies: string[];
  /** The viewer picked at least one signal. */
  canMatchMySignals: boolean;
  /** The viewer is assigned to a track. */
  canMatchMyTrack: boolean;
}) {
  const panelId = useId();
  const count = activeFilterCount(filters);
  const set = (patch: Partial<DirectoryFilters>) =>
    onChange({ ...filters, ...patch });
  // In the organiser's order, whatever order the caller holds them in.
  const signalOptions = useMemo(
    () =>
      sortSignals(signals).map((signal) => ({
        value: signal.id,
        label: signal.label,
      })),
    [signals],
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          value={filters.query}
          onChange={(event) => set({ query: event.target.value })}
          placeholder="Search by name, role, or company"
          aria-label="Search attendees"
          // 16px text on a phone: anything smaller makes iOS zoom the page.
          className="h-[3.25rem] w-full min-w-0 rounded-2xl border border-input bg-background pl-12 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:border-lit focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-lit/25"
          data-testid="attendee-search"
        />
      </div>

      {/* `-m-1 p-1` leaves room for the focus ring, which the scroller clips. */}
      <div
        className="-m-1 flex gap-2 overflow-x-auto p-1 [scrollbar-width:thin] sm:flex-wrap sm:overflow-visible"
        data-testid="attendee-chip-row"
      >
        <ToggleChip
          selected={panelOpen || count > 0}
          aria-pressed={undefined}
          aria-expanded={panelOpen}
          aria-controls={panelId}
          onClick={() => onPanelOpenChange(!panelOpen)}
          className={`${ROW_CHIP} ${chipState(panelOpen || count > 0)} inline-flex items-center gap-2`}
          data-testid="attendee-filters-toggle"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters{count > 0 ? ` (${count})` : ""}
        </ToggleChip>
        {canMatchMySignals && (
          <ToggleChip
            selected={filters.sameSignalsAsMe}
            onClick={() => set({ sameSignalsAsMe: !filters.sameSignalsAsMe })}
            className={`${ROW_CHIP} ${chipState(filters.sameSignalsAsMe)}`}
            data-testid="filter-same-signals"
          >
            Same signals as me
          </ToggleChip>
        )}
        {canMatchMyTrack && (
          <ToggleChip
            selected={filters.sameTrackAsMe}
            onClick={() => set({ sameTrackAsMe: !filters.sameTrackAsMe })}
            className={`${ROW_CHIP} ${chipState(filters.sameTrackAsMe)}`}
            data-testid="filter-same-track"
          >
            Same track as me
          </ToggleChip>
        )}
      </div>

      {panelOpen && (
        <div
          id={panelId}
          className="space-y-4 rounded-2xl border border-border bg-card p-4"
          data-testid="attendee-filters-panel"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FilterSelect
              label="Company"
              allLabel="All companies"
              value={filters.company}
              options={companies.map((value) => ({ value, label: value }))}
              onChange={(company) => set({ company })}
              testId="filter-company"
            />
            {signals.length > 0 && (
              <FilterSelect
                label="Signal"
                allLabel="All signals"
                value={filters.signalIds[0] ?? ""}
                options={signalOptions}
                onChange={(signalId) =>
                  set({ signalIds: signalId ? [signalId] : [] })
                }
                testId="filter-signal"
              />
            )}
            {tracks.length > 0 && (
              <FilterSelect
                label="Track"
                allLabel="All tracks"
                value={filters.trackId}
                options={tracks.map((track) => ({
                  value: track.id,
                  label: track.name,
                }))}
                onChange={(trackId) => set({ trackId })}
                testId="filter-track"
              />
            )}
          </div>

          {count > 0 && (
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              onClick={() => onChange({ ...NO_FILTERS, query: filters.query })}
              data-testid="attendee-filters-clear"
            >
              <X className="mr-2 h-4 w-4" />
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
  testId,
}: {
  label: string;
  allLabel: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  testId: string;
}) {
  const id = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={SELECT}
        data-testid={testId}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
